import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckCircle, AlertCircle } from 'lucide-react';

export default function ParentJoinPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [code, setCode] = useState(searchParams.get('code') ?? '');
  const [debouncedCode, setDebouncedCode] = useState(code);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Debounce code input so we don't preview on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedCode(code.trim().toUpperCase()), 400);
    return () => clearTimeout(t);
  }, [code]);

  const preview = useQuery(
    api.parent.redeemInviteCode.preview,
    debouncedCode.length === 8 ? { code: debouncedCode } : 'skip'
  );

  const redeem = useMutation(api.parent.redeemInviteCode.redeem);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!consentChecked) {
      setError('Please confirm parental consent to continue.');
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const result = await redeem({ code: code.trim(), consentGiven: true });
      setSuccess(true);
      setTimeout(() => navigate(`/parent/children/${result.playerId}`), 1500);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (success) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="text-center space-y-3">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
          <h2 className="text-xl font-bold text-foreground">You're linked!</h2>
          <p className="text-muted-foreground">Taking you to your child's profile…</p>
        </div>
      </div>
    );
  }

  const isValidCode = preview?.valid === true;
  const codeError =
    debouncedCode.length === 8 && preview && !preview.valid
      ? preview.reason === 'already_claimed'
        ? 'This code has already been used.'
        : preview.reason === 'expired'
          ? 'This code has expired. Ask your coach for a new one.'
          : 'Code not found. Check the code and try again.'
      : null;

  return (
    <div className="max-w-md mx-auto p-4 py-10 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Join your child's profile</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Enter the invite code from your coach to link your account.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Code input */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Invite code</label>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 8))}
            placeholder="e.g. XK7M2P9Q"
            className="font-mono text-lg tracking-widest uppercase"
            maxLength={8}
            required
          />
          {codeError && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" /> {codeError}
            </p>
          )}
          {isValidCode && (
            <p className="text-sm text-green-700 flex items-center gap-1">
              <CheckCircle className="w-4 h-4" />
              Linking to <strong className="ml-1">{preview.playerName}</strong>
            </p>
          )}
        </div>

        {/* Consent checkbox */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={consentChecked}
            onChange={(e) => setConsentChecked(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-muted-foreground">
            I confirm I have parental or guardian consent to create this profile and receive
            updates about this player.{' '}
            <Link to="/privacy" className="underline text-blue-600 hover:text-blue-800">
              Privacy Policy
            </Link>
          </span>
        </label>

        {error && (
          <p className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" /> {error}
          </p>
        )}

        <Button
          type="submit"
          className="w-full"
          disabled={isSubmitting || !consentChecked || code.trim().length < 8}
        >
          {isSubmitting ? 'Linking…' : 'Link my account'}
        </Button>
      </form>

      <p className="text-xs text-center text-muted-foreground">
        Don't have a code? Ask your coach to generate one from the player's profile.
      </p>
    </div>
  );
}
